import { CaretDown } from '@phosphor-icons/react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { cva } from 'class-variance-authority'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import React, { useEffect, useMemo, useState } from 'react'

import { SpinnerIcon } from '@/components/shared/ui/Icons'

const buttonVariants = cva(
  'relative inline-flex shrink-0 items-center truncate text-sm font-medium focus:outline-none focus:ring-0',
  {
    variants: {
      variant: {
        primary:
          'border border-brand border-opacity-70 bg-brand text-snow shadow hover:bg-primary-dark focus:border-brand dark:text-gray12 dark:hover:bg-brand',
        warning:
          'border border-yellow9 bg-yellow8 text-snow shadow hover:bg-yellow9 hover:text-snow focus:border-yellow9 dark:text-gray12',
        success:
          'border border-green9 bg-green8 text-snow shadow hover:bg-green9 hover:text-snow focus:border-green9 dark:text-gray12',
        danger:
          'border border-red10 bg-red8 text-snow shadow hover:bg-red9 focus:border-red9 dark:text-gray12',
      },
      size: {
        xs: 'h-7 px-2 text-xs',
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-base',
        lg: 'h-12 px-6 text-lg',
        xl: 'h-14 px-8 text-xl',
      },
      rounded: {
        xs: 'rounded-sm',
        sm: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'xs',
      rounded: 'md',
    },
  }
)

const triggerVariants = cva(
  'inline-flex items-center justify-center border-l border-white/20',
  {
    variants: {
      variant: {
        primary:
          'border border-brand border-opacity-70 bg-brand text-snow shadow hover:bg-primary-dark focus:border-brand dark:text-gray12 dark:hover:bg-brand',
        warning:
          'border border-yellow9 bg-yellow8 text-snow shadow hover:bg-yellow9 hover:text-snow focus:border-yellow9 dark:text-gray12',
        success:
          'border border-green9 bg-green8 text-snow shadow hover:bg-green9 hover:text-snow focus:border-green9 dark:text-gray12',
        danger:
          'border border-red10 bg-red8 text-snow shadow hover:bg-red9 focus:border-red9 dark:text-gray12',
      },
      size: {
        xs: 'h-7 w-7',
        sm: 'h-8 w-8',
        md: 'h-10 w-10',
        lg: 'h-12 w-12',
        xl: 'h-14 w-14',
      },
      rounded: {
        xs: 'rounded-r-sm',
        sm: 'rounded-r',
        md: 'rounded-r-md',
        lg: 'rounded-r-lg',
        xl: 'rounded-r-xl',
        full: 'rounded-r-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'xs',
      rounded: 'md',
    },
  }
)

interface ButtonDropdownItemPropType {
  children?: React.ReactNode
  disabled?: boolean
  itemTitle?: React.ReactNode
  itemDescription?: string
  onSelect?: (e: any) => any
  onChange?: (key: string) => any
  variant?: 'primary' | 'warning' | 'success' | 'danger'
  itemKey: string
}

export const ButtonDropdownItem = ({
  children,
  disabled,
  itemTitle,
  itemDescription,
  onSelect,
  onChange,
  itemKey,
  ...props
}: ButtonDropdownItemPropType) => {
  return (
    <DropdownMenu.Item
      className={clsx(
        'cursor-pointer px-2 py-1.5 text-sm outline-none hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-700',
        disabled && 'pointer-events-none'
      )}
      disabled={disabled}
      onSelect={(data) => {
        if (onSelect) {
          const result = onSelect(data)
          if (result !== true) {
            if (onChange) onChange(itemKey)
          }
        }
      }}
      {...props}
    >
      {!children && (
        <div className='px-1.5 py-1'>
          <p className='truncate text-sm dark:text-gray-200'>{itemTitle}</p>
          <p className='mt-1 text-xs text-gray11 dark:text-gray-400'>
            {itemDescription}
          </p>
        </div>
      )}
      {Boolean(children) && children}
    </DropdownMenu.Item>
  )
}

interface IButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  disabled?: boolean
}
interface IButtonDropdownProps {
  children?:
    | ReactElement<ButtonDropdownItemPropType>
    | Array<ReactElement<ButtonDropdownItemPropType> | null>
  onClick: (key: string) => any
  selectedKey?: string
  loading?: boolean
  hideSelected?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  rounded?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  buttonProps?: IButtonProps
}

export default function ButtonDropdown({
  children,
  onClick,
  selectedKey,
  loading,
  hideSelected,
  size = 'xs',
  buttonProps = {},
  rounded = 'md',
}: IButtonDropdownProps) {
  const [currentSelectedKey, setCurrentSelectedKey] = useState(selectedKey)

  const childrenArray = useMemo(() => {
    if (!children) return []
    if (Array.isArray(children))
      return children.filter((child) => {
        return child?.type?.toString() !== 'Symbol(react.fragment)'
      })
    return [children]
  }, [children])

  const currentChild = useMemo<
    ReactElement<ButtonDropdownItemPropType> | null | undefined
    // @ts-ignore
  >(() => {
    if (!currentSelectedKey) return childrenArray[0]
    return childrenArray.find(
      (child) => child?.props?.itemKey === currentSelectedKey
    )
  }, [currentSelectedKey])

  useEffect(() => {
    if (selectedKey) {
      setCurrentSelectedKey(selectedKey)
    }
  }, [selectedKey])

  const handleSelect = (itemKey: string) => {
    setCurrentSelectedKey(itemKey)
  }

  const variant = currentChild?.props.variant || 'primary'

  return (
    <div className='inline-flex'>
      <button
        className={clsx(
          buttonVariants({ variant, size, rounded }),
          childrenArray.length > 1 && 'rounded-r-none'
        )}
        {...buttonProps}
        onClick={() =>
          // @ts-ignore
          onClick(currentChild.itemKey)
        }
        disabled={loading}
      >
        {loading ? (
          <SpinnerIcon className='h-4 w-4 animate-spin !text-gray1 dark:!text-gray-200' />
        ) : (
          currentChild?.props?.itemTitle
        )}
      </button>
      {childrenArray.length > 1 && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            disabled={loading}
            className={triggerVariants({ variant, size, rounded })}
          >
            <CaretDown weight='bold' />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align='end'
              sideOffset={5}
              className='z-[6] min-w-[8rem] overflow-hidden rounded-md border border-gray-200 bg-white p-1 shadow-md dark:border-gray-700 dark:bg-gray-800'
            >
              <>
                {childrenArray.map((child) => {
                  if (
                    child?.props?.itemKey === currentSelectedKey &&
                    hideSelected
                  )
                    return null
                  if (React.isValidElement(child)) {
                    return React.cloneElement(child, { onChange: handleSelect })
                  }
                  return child
                })}
              </>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </div>
  )
}
